// src/components/Admin/Donors/UpdateDonationStatusModal.tsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
  Divider,
  Paper,
  Card,
  useTheme,
  alpha,
  CircularProgress,
  Stack,
  Chip,
  FormHelperText,
  TextField,
  Alert,
  Fade,
} from "@mui/material";
import CustomModal from "../Common/Modal";
import type { DonationStatus } from "../../../types/donation";

// Icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";
import SyncIcon from "@mui/icons-material/Sync";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

interface UpdateDonationStatusModalProps {
  open: boolean;
  currentStatus: DonationStatus | null;
  onClose: () => void;
  onUpdate: (newStatus: DonationStatus, reason?: string) => void;
}

const statusOptions: {
  value: DonationStatus;
  label: string;
  color: string;
  icon: React.ReactNode;
  description: string;
}[] = [
  { 
    value: "pending", 
    label: "Pending", 
    color: "#ff9800", 
    icon: <PendingIcon />,
    description: "Payment is being processed but not yet confirmed"
  },
  { 
    value: "processing", 
    label: "Processing", 
    color: "#2196f3", 
    icon: <SyncIcon />,
    description: "Payment is actively being processed by the payment provider"
  },
  { 
    value: "completed", 
    label: "Completed", 
    color: "#4caf50", 
    icon: <CheckCircleIcon />,
    description: "Payment has been successfully processed and confirmed"
  },
  { 
    value: "failed", 
    label: "Failed", 
    color: "#f44336", 
    icon: <CancelIcon />,
    description: "Payment processing failed due to an error"
  },
  { 
    value: "refunded", 
    label: "Refunded", 
    color: "#9c27b0", 
    icon: <WarningAmberIcon />,
    description: "The donation amount has been refunded to the donor"
  },
  { 
    value: "cancelled", 
    label: "Cancelled", 
    color: "#607d8b", 
    icon: <CancelIcon />,
    description: "The donation was cancelled by the donor or administrator" 
  },
];

const getStatusOption = (status: DonationStatus | null) => {
  return statusOptions.find(option => option.value === status) || null;
};

const UpdateDonationStatusModal: React.FC<UpdateDonationStatusModalProps> = ({
  open,
  currentStatus,
  onClose,
  onUpdate,
}) => {
  const theme = useTheme();
  const [selectedStatus, setSelectedStatus] = useState<DonationStatus | "">("");
  const [reason, setReason] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Helper to get the status info
  const currentStatusOption = getStatusOption(currentStatus);
  const selectedStatusOption = getStatusOption(selectedStatus as DonationStatus);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSelectedStatus("");
      setReason("");
      setCustomReason("");
    }
  }, [open]);

  const handleStatusChange = (event: SelectChangeEvent<DonationStatus>) => {
    setSelectedStatus(event.target.value as DonationStatus);
  };

  const handleReasonChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setReason(value);
    if (value !== "Other") {
      setCustomReason("");
    }
  };

  const handleSubmit = () => {
    if (selectedStatus) {
      setIsLoading(true);
      // Pass the custom reason if "Other" is selected, otherwise use the predefined reason
      const finalReason = reason === "Other" ? customReason : reason;
      onUpdate(selectedStatus, finalReason);
      // In a real implementation, we'd wait for the API response
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  return (
    <CustomModal 
      open={open} 
      title="Update Donation Status" 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <Box>
        {currentStatus && (
          <Fade in={!!currentStatus}>
            <Alert 
              severity="info" 
              variant="outlined"
              icon={false}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2">Current Status:</Typography>
                <Chip
                  icon={currentStatusOption?.icon}
                  label={currentStatusOption?.label || currentStatus}
                  sx={{
                    bgcolor: alpha(currentStatusOption?.color || theme.palette.grey[500], 0.1),
                    color: currentStatusOption?.color || theme.palette.text.primary,
                    fontWeight: 600,
                  }}
                  size="medium"
                />
              </Stack>
            </Alert>
          </Fade>
        )}

        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Select New Status
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                value={selectedStatus}
                label="Status"
                onChange={handleStatusChange}
                sx={{ borderRadius: 1 }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 2,
                      maxHeight: 300,
                      boxShadow: theme.shadows[4],
                    }
                  }
                }}
              >
                <MenuItem value="" disabled>
                  Select status
                </MenuItem>
                {statusOptions.map((option) => (
                  <MenuItem 
                    key={option.value} 
                    value={option.value}
                    sx={{ 
                      py: 1.5,
                      '&.Mui-selected': {
                        bgcolor: alpha(option.color, 0.1),
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: alpha(option.color, 0.15),
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" width="100%">
                      <Box sx={{ color: option.color }}>
                        {option.icon}
                      </Box>
                      <Box flex={1}>
                        <Typography variant="body1" fontWeight={500}>
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Choose the new status for this donation
              </FormHelperText>
            </FormControl>

            {selectedStatus && (
              <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Chip
                  icon={currentStatusOption?.icon}
                  label={currentStatusOption?.label || currentStatus}
                  sx={{
                    bgcolor: alpha(currentStatusOption?.color || theme.palette.grey[500], 0.1),
                    color: currentStatusOption?.color || theme.palette.text.primary,
                    fontWeight: 500,
                  }}
                />
                <ArrowForwardIcon sx={{ mx: 1, color: theme.palette.text.secondary }} />
                <Chip
                  icon={selectedStatusOption?.icon}
                  label={selectedStatusOption?.label}
                  sx={{
                    bgcolor: alpha(selectedStatusOption?.color || theme.palette.grey[500], 0.1),
                    color: selectedStatusOption?.color || theme.palette.text.primary,
                    fontWeight: 500,
                  }}
                />
              </Box>
            )}
          </Box>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Reason for Change
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 3 }}>
            <FormControl fullWidth sx={{ mb: reason === "Other" ? 2 : 0 }}>
              <InputLabel>Reason</InputLabel>
              <Select
                value={reason}
                onChange={handleReasonChange}
                label="Reason"
                sx={{ borderRadius: 1 }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value="Incorrect Amount">Incorrect Amount</MenuItem>
                <MenuItem value="Duplicate Donation">Duplicate Donation</MenuItem>
                <MenuItem value="Fraudulent Activity">Fraudulent Activity</MenuItem>
                <MenuItem value="Donor Request">Donor Request</MenuItem>
                <MenuItem value="Payment Method Issue">Payment Method Issue</MenuItem>
                <MenuItem value="Other">Other (Specify)</MenuItem>
              </Select>
              <FormHelperText>
                Provide a reason for changing the donation status
              </FormHelperText>
            </FormControl>

            {reason === "Other" && (
              <TextField
                fullWidth
                label="Specify Reason"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                multiline
                rows={2}
                variant="outlined"
                placeholder="Enter detailed reason..."
              />
            )}
          </Box>
        </Card>

        <Divider sx={{ my: 3 }} />

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={onClose}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!selectedStatus || (reason === "Other" && !customReason) || isLoading}
            sx={{ borderRadius: 2, px: 3 }}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {isLoading ? "Updating..." : "Update Status"}
          </Button>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default UpdateDonationStatusModal;
